import React, { useEffect } from "react";
import PackageBundleItem from "@/components/ProductPackages/PackageBundleItem";
import { useProductData } from "@/zustand/productStore";
import { useMarketingStore } from "@/zustand/emailMarketingStore";
import { ProductPackageItem } from "@children/components/ProductPackages/ProductPackageItem";
import { ProductPackageItemV2 } from "@children/components/ProductPackages/ProductPackageItemV2";
import { useCartData } from "@/zustand/cartStore";
import SubscriptionBox from "@/components/Subscription/SubscriptionBox";
import PurchaseTypeSelector from "@/components/Subscription/PurchaseTypeSelector";
import { useSubscribeProducts } from "@/hooks/useSubscribeProducts";
import { useDefaultProductData } from "@/hooks/useDefaultProductData";

// Backward-compatible: old ACF true/false field → true="v1", false="v2" (no DB migration needed).
// New ACF select field: "1"="v1", "0"="v2", "v3"="v3". "0" is truthy in JS so must be explicit.
const getSubscriptionUIVersion = (value) => {
  if (value === true || value === "v1" || value === "1" || value === 1) return "v1";
  if (value === "v3") return "v3";
  if (value === "v4") return "v4";
  return "v2"; // false, null, undefined, "0", or any unrecognized value
};

const ProductsList = ({ bundles, pickerV2, subscription }) => {
  const { setSelectedProduct, selectedProduct, setVariants, setWarrantyState, isSubscribe, setIsSubscribe } =
    useCartData();
  const { freeShippingApplied } = useMarketingStore();
  const productData = useProductData();
  const { shouldUseBundleTitle } = useProductData();
  const freeShippingProducts = productData?.products?.some((product) => product.display_name.includes("+"));

  // templates & colors
  const base = window?.subscription_template;
  const productsContent = window.post_template?.checkout_v4_products;
  const textSale = productsContent?.checkout_v4_products_textSale;
  const bestSellerBackground = productsContent?.checkout_v4_products_best_seller;
  const bestValueBackground = productsContent?.checkout_v4_products_best_value;
  const bestSellerTextColor = productsContent?.checkout_v4_products_best_seller_text_color;
  const bestValueTextColor = productsContent?.checkout_v4_products_best_value_text_color;
  const textBadge = productsContent?.checkout_v4_products_badgeTextColor;
  const cardButtonColor = window?.post_template?.checkout_v4_products?.checkout_v4_products_checkButtonCcColor;
  const cardBackground = productsContent.checkout_v4_products_CardBackground;
  const cardBackgroundSelected = productsContent.checkout_v4_products_selectedCardBackground;
  const cardBorder = productsContent.checkout_v4_products_borderColor;
  const cardBorderSelected = productsContent.checkout_v4_products_borderColorSelected;
  const textPrimary = productsContent.checkout_v4_products_textPrimary;
  const textSecondary = productsContent.checkout_v4_products_textSecondary;
  const thankYouPageProductName = base?.tr_thankyou_page_product_name;
  const subscriptionUIVersion = subscription
    ? getSubscriptionUIVersion(productData?.subscription?.subscription_ui)
    : null;

  document.documentElement.style.setProperty("--checkbutton-cc-color", cardButtonColor);
  document.documentElement.style.setProperty("--card-background-active", cardBackgroundSelected);
  document.documentElement.style.setProperty("--card-border-active", cardBorderSelected);
  document.documentElement.style.setProperty("--card-border", cardBorder);
  document.documentElement.style.setProperty("--card-background", cardBackground);
  document.documentElement.style.setProperty("--card-text-primary", textPrimary);
  document.documentElement.style.setProperty("--card-text-secondary", textSecondary);
  document.documentElement.style.setProperty("--sale-text", textSale);
  document.documentElement.style.setProperty("--best-seller-background", bestSellerBackground);
  document.documentElement.style.setProperty("--best-value-background", bestValueBackground);
  document.documentElement.style.setProperty("--best-value-text", bestValueTextColor);
  document.documentElement.style.setProperty("--best-seller-text", bestSellerTextColor);
  document.documentElement.style.setProperty("--text-badge", textBadge);

  useEffect(() => {
    if (freeShippingApplied) {
      productData.changeProductsShippingPrice(0);
    }
  }, [freeShippingApplied, productData]);

  const getSubscriptionProductField = (product, field) => {
    const list = productData.subscription?.products;
    if (!Array.isArray(list)) return null;

    const match = list.find((p) => p.amount === product.amount);
    return match?.[field] ?? null;
  };

  const handleChange = React.useCallback(
    (product) => {
      const bundleTitle = shouldUseBundleTitle ? product.bundle_title : null;

      let shipping_konnektive_id = product.shipping_konnektive_id ?? null;
      let warranty_product_id = product.warranty_product_id ?? null;

      if (isSubscribe) {
        shipping_konnektive_id =
          getSubscriptionProductField(product, "shipping_konnektive_id") ?? shipping_konnektive_id;
        warranty_product_id = getSubscriptionProductField(product, "warranty_product_id") ?? warranty_product_id;
      }

      setSelectedProduct({
        sku: product.sku?.trim(),
        amount: product.amount,
        billed_amount: product.billed_amount,
        konnektive_id: product.konnektive_id?.trim(),
        bundle_products: product.bundle_products,
        warranty_price: product.warranty_price,
        warranty_product_id,
        display_name: product.display_name,
        generic_name: product.generic_name,
        bundle_title: bundleTitle,
        price: product.price,
        old_price: product.old_price,
        shipping_price: product.shipping_price,
        promo_discounted_price: product.promo_discounted_price,
        promo_shipping_price: product.promo_shipping_price,
        fake_products: product.fake_products,
        enable_fake_products: product.enable_fake_products,
        shipping_konnektive_id,
        thankYouPageProductName,
        isSubscribe,
      });
    },
    [isSubscribe, setSelectedProduct, shouldUseBundleTitle, thankYouPageProductName]
  );

  useSubscribeProducts(productData, setIsSubscribe);

  useDefaultProductData({
    productData,
    isSubscribe,
    selectedProduct,
    setVariants,
    setWarrantyState,
    handleChange,
  });

  return (
    <>
      {subscriptionUIVersion === "v2" && <PurchaseTypeSelector />}
      {subscriptionUIVersion === "v3" && <PurchaseTypeSelector variant="v3" showDescription={false} />}
      {subscriptionUIVersion === "v4" && <PurchaseTypeSelector variant="v4" showDescription={false} />}

      {productData?.products &&
        productData?.products.map((product, i) => {
          switch (true) {
            case bundles:
              return (
                <PackageBundleItem
                  key={i}
                  product={product}
                  handleChange={() => handleChange(product)}
                  active={selectedProduct?.price === product?.price}
                  index={i}
                />
              );

            case pickerV2:
            case subscription: {
              const threeXProduct = productData?.products?.find((p) => p.amount == 3);
              return (
                <ProductPackageItemV2
                  key={i}
                  product={product}
                  handleChange={() => handleChange(product)}
                  active={selectedProduct?.price === product?.price}
                  index={i}
                  onUpgrade={threeXProduct && i < 2 ? () => handleChange(threeXProduct) : undefined}
                />
              );
            }

            default:
              return (
                <ProductPackageItem
                  key={i}
                  product={product}
                  handleChange={() => handleChange(product)}
                  active={selectedProduct?.price === product?.price}
                  index={i}
                  globalShippingLabel={freeShippingProducts}
                />
              );
          }
        })}

      {subscriptionUIVersion === "v1" && <SubscriptionBox />}

      {subscriptionUIVersion === "v3" && (
        <div className="purchaseTypeSelectorV3__description">
          {isSubscribe ? base?.tr_subscription_description : base?.tr_onetime_description}
        </div>
      )}
    </>
  );
};

export default ProductsList;
